import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LocalStorage } from 'angular-web-storage';
import { Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { EventType } from '../../../../../../../models/event-type';
import { ListResponse } from '../../../../../../../models/list-response';
import { WaitlistEntry, WaitlistEntryStatus } from '../../../../../../../models/scheduled-event';
import { CallbackResult, CallbackResultStatus, dateStringFromISO8601, getAge, timeStringFromISO8601 } from '../../../../../../../utils';
import { EventTypeService } from '../../../../../../../services/event-type/event-type.service';
import { Customer, CustomerActiveProficiency, CustomerMembershipIcon, CustomerProfileDetails } from '../../../../../../../models/customer';
import { CustomerService } from '../../../../../../../services/customer/customer.service';
import { openClimberAppInNewTabAsCustomer, openClimberAppInNewTabAsStaff } from '../../../../../../../utils-models';
import {
  InviteWaitlistEntryToBookModalComponent
} from '../../events-list/event-detail/sub-lists/event-waitlist-sub-list/modals/invite-waitlist-entry-to-book-modal/invite-waitlist-entry-to-book-modal.component';
import {
  ModifyPendingWaitlistEntryModalComponent
} from '../../events-list/event-detail/sub-lists/event-waitlist-sub-list/modals/modify-waitlist-entry-booking-deadline-modal/modify-pending-waitlist-entry-modal.component';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';
import { ExportEventWaitlistModalComponent } from '../../../modals/export-event-waitlist-modal/export-event-waitlist-modal.component';
import {
  AddToEventWaitlistModalComponent
} from '../../events-list/event-detail/sub-lists/event-waitlist-sub-list/modals/add-to-event-waitlist-modal/add-to-event-waitlist-modal.component';
import { ScheduleType } from '../../../../../../../models/event-type-enums';
import { AddToRosterModalComponent } from '../../../modals/add-to-roster-modal/add-to-roster-modal.component';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import {
  InviteWaitlistEntryToRosterModalComponent
} from '../../events-list/event-detail/sub-lists/event-waitlist-sub-list/modals/invite-waitlist-entry-to-roster-modal/invite-waitlist-entry-to-roster-modal.component';
import { SimpleMessageModalComponent } from '../../../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import { ScheduledEventService } from '../../../../../../../services/scheduled-event/scheduled-event.service';
import { LocationService } from '../../../../../../../services/location/location.service';


@Component({
  selector: 'app-event-type-waitlist-entries',
  templateUrl: './event-type-waitlist-entries.component.html',
  styleUrls: ['./event-type-waitlist-entries.component.css']
})
export class EventTypeWaitlistEntriesComponent implements OnInit {
  public eventTypeIdInt: number;
  public eventType: EventType = null;

  @ViewChild('waitlistTableWrapper') waitlistTableWrapper;
  @ViewChild('waitlistTableTitleContainer') waitlistTableTitleContainer;

  public search = '';
  public isActive = true;
  public pageNumber = 1;
  @LocalStorage() public eventTypeWaitlistPageSize;

  public loadWaitlistPageSubject = new Subject<null>();

  public waitListEntryResult: ListResponse<WaitlistEntry>;

  public loadingEventType = true;
  public loadingWaitlistEntries = true;

  public customer: {[id: number]: Customer} = {};
  public customerProfileDetails: {[id: number]: CustomerProfileDetails} = {};
  public loadingCustomer: {[id: number]: boolean} = {};
  public loadingCustomerProfileDetails: {[id: number]: boolean} = {};
  public customerNotFound: {[id: number]: boolean} = {};

  public eventTypeNotFound = false;
  public serverError: number = null;

  // The most recent result returned from a modal.
  public modalResult: CallbackResult = null;

  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly timeStringFromISO8601 = timeStringFromISO8601;
  public readonly getAge = getAge;
  public readonly SEStatus = WaitlistEntryStatus;
  public readonly CallbackResultStatus = CallbackResultStatus;
  public readonly ScheduleType = ScheduleType;

  private waitlistRequestCount = 0;

  constructor(
    public locationService: LocationService,
    private route: ActivatedRoute,
    private eventTypeService: EventTypeService,
    private scheduledEventService: ScheduledEventService,
    private customerService: CustomerService,
    private orgService: OrganizationConfigService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    if (!this.eventTypeWaitlistPageSize) {
      this.eventTypeWaitlistPageSize = 10;
    }

    const routeParams = this.route.snapshot.params;
    this.eventTypeIdInt = parseInt(routeParams.id, 10);
    if (isNaN(this.eventTypeIdInt)) {
      this.eventTypeNotFound = true;
      return;
    }

    this.loadWaitlistPageSubject.pipe(
      map(() => { this.loadingWaitlistEntries = true; }),
      // Avoid rapid requests by waiting 300ms before loading.
      debounceTime(300)
    ).subscribe(() => {
      this.loadWaitlistPage();
    });

    this.loadEventType();
    this.loadWaitlistPage();
  }

  @HostListener('window:resize')
  onResize() {
    this.updateTableTitleContainerWidth();
  }

  public setPage(newPageNumber: number) {
    this.pageNumber = newPageNumber;
    this.loadWaitlistPageSubject.next();
  }

  public lastPageNumber() {
    if (!this.waitListEntryResult) { return 1; }
    return Math.ceil(this.waitListEntryResult.count / this.eventTypeWaitlistPageSize);
  }

  public resultsPresent(): boolean {
    return (
      this.waitListEntryResult &&
      this.waitListEntryResult.results &&
      (this.waitListEntryResult.results.length > 0)
    );
  }

  public openExportWaitlistModal() {
    this.modalResult = null;

    const modalRef = this.modalService.open(ExportEventWaitlistModalComponent);
    modalRef.componentInstance.waitlistEntries = this.waitListEntryResult.results;
    modalRef.componentInstance.savedCallback = () => {
      this.modalResult = {
        text: 'Waitlist exported successfully.',
        status: CallbackResultStatus.success
      };
    };
  }

  public openAddToWaitlistModal() {
    this.modalResult = null;

    const modalRef = this.modalService.open(AddToEventWaitlistModalComponent);
    modalRef.componentInstance.eventType = this.eventType;
    modalRef.componentInstance.addedToWaitlistCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadWaitlistPageSubject.next();
    };
  }

  public handleCompleteBooking(entry: WaitlistEntry) {
    if (entry.booking_customer_id === null) {
      this.customerService
        .getStaffClimberAppToken()
        .then(token => {
          openClimberAppInNewTabAsStaff(
            token,
            this.orgService.getOrganizationConfig().climber_app_base_url,
            (
              entry.event.event_schedule_from_waitlist_url
              + entry.id
              + '/?use_saved_response=false'
            )
          );
        })
        .catch(reason => {
          this.serverError = reason.status;
        });
    } else {
      this.customerService
        .getCustomerClimberAppToken(entry.booking_customer_id)
        .then(token => {
          openClimberAppInNewTabAsCustomer(
            token,
            entry.booking_customer_id,
            this.orgService.getOrganizationConfig().climber_app_base_url,
            (
              entry.event.event_schedule_from_waitlist_url
              + entry.id
              + '/?use_saved_response=false'
            )
          );
        })
        .catch(reason => {
          this.serverError = reason.status;
        });
    }
  }

  public handlePurchaseRosterMembership(entry: WaitlistEntry) {
    if (entry.booking_customer_id === null) {
      this.customerService
        .getStaffClimberAppToken()
        .then(token => {
          openClimberAppInNewTabAsStaff(
            token,
            this.orgService.getOrganizationConfig().climber_app_base_url,
            (
              this.orgService.getOrganizationConfig().climber_app_base_url
              + 'membership/initialize-for-waitlist-entry/'
              + entry.id
              + '/'
            )
          );
        })
        .catch(reason => {
          this.serverError = reason.status;
        });
    } else {
      this.customerService
        .getCustomerClimberAppToken(entry.booking_customer_id)
        .then(token => {
          openClimberAppInNewTabAsCustomer(
            token,
            entry.booking_customer_id,
            this.orgService.getOrganizationConfig().climber_app_base_url,
            (
              this.orgService.getOrganizationConfig().climber_app_base_url
              + 'membership/initialize-for-waitlist-entry/'
              + entry.id
              + '/'
            )
          );
        })
        .catch(reason => {
          this.serverError = reason.status;
        });
    }
  }

  public openInviteToBookModal(entry: WaitlistEntry) {
    this.modalResult = null;

    let modalRef: NgbModalRef;

    if (this.eventType.schedule_type === ScheduleType.rolling) {
      modalRef = this.modalService.open(InviteWaitlistEntryToRosterModalComponent);
    } else {
      modalRef = this.modalService.open(InviteWaitlistEntryToBookModalComponent);
    }

    modalRef.componentInstance.waitlistEntry = entry;
    modalRef.componentInstance.inviteSentCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadWaitlistPageSubject.next();
    };
  }

  public openAddToRosterModal(entry: WaitlistEntry) {
    this.modalResult = null;

    const modalRef = this.modalService.open(AddToRosterModalComponent);
    modalRef.componentInstance.eventTypeId = this.eventType.id;
    modalRef.componentInstance.eventTypeName = this.eventType.name;
    modalRef.componentInstance.preselectedOwnerCustomerId = entry.booking_customer_id;
    modalRef.componentInstance.preselectedParticipantCustomerId = entry.participant_customer_id;
  }

  public openRemoveWaitlistEntryModal(entry: WaitlistEntry) {
    this.modalResult = null;

    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Remove Waitlist Entry';
    modalRef.componentInstance.messageText = (
      'Are you sure you want to remove the wait list entry for ' +
      entry.first_name + ' ' + entry.last_name +
      '? This action cannot be undone.'
    );
    modalRef.componentInstance.messageAlertClass = 'alert-danger';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      if (entry.participant_customer_id) {
        this.loadingCustomer[entry.participant_customer_id] = true;
        this.loadingCustomerProfileDetails[entry.participant_customer_id] = true;
      }
      this.scheduledEventService
        .removeWaitlistEntry(entry.id)
        .then(() => {
          this.modalResult = {
            text: (
              'Waitlist entry for ' +
              entry.first_name + ' ' + entry.last_name +
              ' removed successfully.'
            ),
            status: CallbackResultStatus.success
          };
        })
        .catch(reason => {
          let errorMessage = '';
          if (reason.status === 403) {
            errorMessage = 'You do not have permission to remove this waitlist entry.'
          } else if (reason.status === 400) {
            errorMessage = reason.error;
          } else {
            errorMessage = 'Error removing waitlist entry: ' + reason.status.toString();
          }
          this.modalResult = {
            text: errorMessage,
            status: CallbackResultStatus.error
          };
        })
        .finally(() => {
          this.loadWaitlistPageSubject.next();
        });
    };
  }

  public openModifyPendingEntryModal(entry: WaitlistEntry) {
    this.modalResult = null;

    const modalRef = this.modalService.open(ModifyPendingWaitlistEntryModalComponent);
    modalRef.componentInstance.waitlistEntry = entry;
    modalRef.componentInstance.actionCompleteCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadWaitlistPageSubject.next();
    };
  }

  public getCustomerProficienciesForId(customerIdInt: number): CustomerActiveProficiency[] {
    if (!this.customerProfileDetails[customerIdInt]) {
      return [];
    }
    return this.customerProfileDetails[customerIdInt].active_proficiencies;
  }

  public getCustomerMembershipIconsForId(customerIdInt: number): CustomerMembershipIcon[] {
    if (!this.customerProfileDetails[customerIdInt]) {
      return [];
    }
    return this.customerProfileDetails[customerIdInt].membership_icons;
  }

  public getPreferredLocationNames(entry: WaitlistEntry): string[] {
    const locNames: string[] = [];

    const preferredLocIdStrs = entry.preferred_locations.split(',');

    const allLocs = this.locationService.getLocations();

    if (allLocs === null) { return []; }

    for (const loc of allLocs) {
      if (preferredLocIdStrs.includes(loc.id.toString())) {
        locNames.push(loc.name);
      }
    }

    return locNames;
  }

  private loadEventType() {
    this.loadingEventType = true;

    this.eventTypeService
      .getEventType(this.eventTypeIdInt)
      .then(response => {
        this.eventType = response;
        setTimeout(
          () => { this.updateTableTitleContainerWidth(); },
          100
        );
      })
      .catch(reason => {
        if (reason.status === 404) {
          this.eventTypeNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventType = false; });
  }

  private loadWaitlistPage() {
    this.loadingWaitlistEntries = true;

    this.waitlistRequestCount++;
    const thisRequestCount = this.waitlistRequestCount;

    this.eventTypeService
      .getEventTypeWaitlistEntries(
        this.eventTypeIdInt,
        this.pageNumber,
        this.eventTypeWaitlistPageSize,
        this.search,
        this.isActive,
        !this.isActive
      )
      .then(response => {
        if (thisRequestCount === this.waitlistRequestCount) {
          this.waitListEntryResult = response;
          this.loadWaitlistCustomerInfo();
        }
      })
      .catch(reason => {
        if (reason.status === 404) {
          this.eventTypeNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.waitlistRequestCount) {
          this.loadingWaitlistEntries = false;
        }
      });
  }

  private loadWaitlistCustomerInfo() {
    for (const entry of this.waitListEntryResult.results) {
      if (entry.participant_customer_id) {
        this.loadCustomer(entry.participant_customer_id);
        this.loadCustomerProfileDetails(entry.participant_customer_id);
      }
    }
  }

  /**
   * Load the customer with the given ID.
   * @param customerIdInt - The API ID of the customer.
   */
  private loadCustomer(customerIdInt: number) {
    this.loadingCustomer[customerIdInt] = true;

    this.customerService
      .getCustomer(customerIdInt)
      .then(customer => {
        this.customer[customerIdInt] = customer;
      })
      .catch(reason => {
        if (reason.status === 404) {
          this.customerNotFound[customerIdInt] = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingCustomer[customerIdInt] = false; });
  }

  /**
   * Load the profile details of the customer with the given ID.
   * @param customerIdInt - The API ID of the customer.
   */
  private loadCustomerProfileDetails(customerIdInt: number) {
    this.loadingCustomerProfileDetails[customerIdInt] = true;

    this.customerNotFound[customerIdInt] = false;

    this.customerService
      .getCustomerProfileDetails(customerIdInt)
      .then(customerProfileDetails => {
        this.customerProfileDetails[customerIdInt] = customerProfileDetails;
      })
      .catch(reason => {
        if (reason.status === 404) {
          this.customerNotFound[customerIdInt] = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingCustomerProfileDetails[customerIdInt] = false;
      });
  }

  private updateTableTitleContainerWidth() {
    this.waitlistTableTitleContainer.nativeElement.style.width =
      this.waitlistTableWrapper.nativeElement.offsetWidth.toString() + 'px';
  }
}
